import { useCallback, useEffect, useMemo, useState } from 'react'
import Button from 'components/Button/Button'
import { BUTTON_TYPES } from 'components/Button/constants'
import { TOption } from 'components/Select/types'
import { Banner } from 'components/Banner/Banner'
import { BTN_TXT } from 'constants/txt'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { Separator } from 'components/Separator/Separator'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import { getRebatePeriodFacilities, getRebatePeriods } from '../api'
import { EditFacilityTiersPopup } from './EditFacilityTiersPopup'
import { FacilityTiersTable } from './FacilityTiersTable'
import { FacilityTierRow, toFacilityTierRows } from './constants'
import { PeriodSelect } from './PeriodSelect'
import './styles.scss'

export const TieredHospitalsSection = ({
  tierOptions
}: {
  tierOptions: TOption[]
}) => {
  const { editFacilityTiersPopup } = useContractDetailsPopup()
  const {
    actions: { getContractDetailsAsync },
    state: { details }
  } = useContractDetailsContext()
  const [periodOptions, setPeriodOptions] = useState<
    (TOption & { isEditable: boolean })[]
  >([])
  const [periodId, setPeriodId] = useState<string>()
  const [facilityRows, setFacilityRows] = useState<FacilityTierRow[]>([])
  const [facilitiesReloadKey, setFacilitiesReloadKey] = useState(0)

  const periodsAreNotGeneratedYet =
    details.rebate?.apply_to_all_hospitals === true && !periodOptions.length

  const canEditPeriod = useMemo(() => {
    return periodOptions.find((option) => option.value === periodId)?.isEditable
  }, [periodId, periodOptions])

  const getPeriods = useCallback(() => {
    const rebateId = details.rebate?.uuid

    if (!details.uuid || !rebateId) {
      setPeriodOptions([])
      setPeriodId(undefined)
      return
    }

    let cancelled = false

    getRebatePeriods(details.uuid, rebateId)
      .then(({ data }) => {
        if (cancelled) return

        setPeriodOptions(
          data.map((period) => ({
            value: period.uuid,
            label: period.label,
            isEditable: period.is_editable
          }))
        )
        setPeriodId(
          (current) =>
            (current && data.some((period) => period.uuid === current)
              ? current
              : data.find((period) => period.is_current)?.uuid) ?? current
        )
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [details.rebate?.uuid, details.uuid])

  useEffect(() => {
    if (details.rebate?.apply_to_all_hospitals !== false) return

    return getPeriods()
  }, [details.rebate?.apply_to_all_hospitals, getPeriods])

  useEffect(() => {
    const rebateId = details.rebate?.uuid

    if (!details.uuid || !rebateId || !periodId || periodsAreNotGeneratedYet) {
      setFacilityRows([])
      return
    }

    let cancelled = false

    getRebatePeriodFacilities(details.uuid, rebateId, periodId)
      .then(({ data }) => {
        if (!cancelled) setFacilityRows(toFacilityTierRows(data))
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [
    details.rebate?.uuid,
    details.uuid,
    facilitiesReloadKey,
    periodsAreNotGeneratedYet,
    periodId
  ])

  const onSaved = useCallback(() => {
    setFacilitiesReloadKey((key) => key + 1)
    getContractDetailsAsync()
  }, [getContractDetailsAsync])

  return (
    <>
      <Separator />
      <RowLayout
        title="Facilities"
        className="tiered-hospitals-section__layout"
      >
        <div className="tiered-hospitals-section column gap-24">
          {periodsAreNotGeneratedYet ? (
            <Banner.Warning text="Save rebate configuration to assign Tiers to Participating Facilities." />
          ) : (
            <>
              <div className="row justify-between align-end">
                <PeriodSelect
                  options={periodOptions}
                  value={periodId}
                  onChange={setPeriodId}
                />
                <Button
                  type={BUTTON_TYPES.DEFAULT}
                  upperCase
                  onClick={() => editFacilityTiersPopup.actions.open()}
                  disabled={!canEditPeriod}
                >
                  {BTN_TXT.EDIT_FACILITY_TIERS}
                </Button>
              </div>
              <FacilityTiersTable
                rows={facilityRows}
                tierOptions={tierOptions}
              />
            </>
          )}
        </div>
      </RowLayout>
      <EditFacilityTiersPopup
        tierOptions={tierOptions}
        periodOptions={periodOptions}
        periodId={periodId}
        onPeriodChange={setPeriodId}
        rows={facilityRows}
        onSaved={onSaved}
      />
    </>
  )
}
